import { NcwOnboardingCongratulationsImage, noop, useAppTheme } from '@capsicum/common';
import { Button, ModalService } from '@capsicum/common-web';
import {
	type NCOnboardingPageProps,
	useCanCreateNCW,
	useVerifyModalRequirements
} from '@capsicum/features/nc-onboarding/shared';
import { useOnboardingSlidesTranslations } from '@capsicum/features/nc-onboarding/web';
import { useMe } from '@capsicum/socios-core';
import { useDeniedRequirementsModal } from '@capsicum/socios-core-web';
import styled, { ThemeProvider } from 'styled-components';

import { Description, Title } from '../onboarding/Page.styled';
import { Shell } from '../shell';
import { VerifyModal } from '../verify/VerifyModal';

type NCOnboardingProps = Pick<NCOnboardingPageProps, 'onDone'> & {
	isNewUser?: boolean;
};

export const CongratulationsScreen = ({ isNewUser = false, onDone }: NCOnboardingProps) => {
	const { allThemes } = useAppTheme();
	const { isMeFetching, refetchMe } = useMe();
	const $t = useOnboardingSlidesTranslations();
	const { isRequirementsLoading, isTravelRuleBlockedBySumsub } = useVerifyModalRequirements();
	const { showDeniedRequirementModal } = useDeniedRequirementsModal({ onDismiss: noop });

	const showVerifyModal = async () => {
		ModalService.clearModals();
		if (isTravelRuleBlockedBySumsub) {
			showDeniedRequirementModal();
		} else {
			await refetchMe();
			ModalService.addModal({
				renderComponent: ({ close }) => <VerifyModal close={close} />
			});
		}
	};

	const { isLoading, onContinue } = useCanCreateNCW({
		isNewUser,
		isRequirementsLoading,
		onDone,
		showVerifyModal
	});

	return (
		<ThemeProvider theme={allThemes.dark}>
			<S.Wrapper>
				<Shell
					cta={
						<Button
							isLoading={isLoading || isMeFetching}
							testID="ncOnboarding-next-button"
							text={$t(
								`ncOnboard.congratulations.actions.${isNewUser ? 'newUsers' : 'existingUsers'}.button`
							)}
							onClick={onContinue}
						/>
					}
					image={NcwOnboardingCongratulationsImage}
				>
					<Title color="onDark.enabled" variant="display2">
						{$t('ncOnboard.congratulations.title')}
					</Title>

					<Description color="onDark.enabled" variant="text2">
						{$t('ncOnboard.congratulations.description')}
					</Description>
				</Shell>
			</S.Wrapper>
		</ThemeProvider>
	);
};
namespace S {
	export const Wrapper = styled.div`
		height: 100dvh;
		background-color: ${({ theme }) => theme.colors.backgrounds.onboarding};
	`;
}
